import {
  BaseAdvancedDateIsBetweenWhereInput,
  BaseMetricPeriodWhereInput,
} from './base-types';
import { dayjs, Dayjs } from './dayjs';

const getCurrentTimeInTimezone = (timezone: string) => dayjs().tz(timezone);

export const createDateWhereInputHelper = (input: { timezone: string }) => {
  const { timezone } = input;
  const currentTimeInTimezone = getCurrentTimeInTimezone(timezone);

  const computeXLastDays = (days: number) => {
    // -1 to include the actual date
    const referenceDateObject = currentTimeInTimezone.subtract(days - 1, 'days');

    return {
      fromDate: referenceDateObject.startOf('day').toDate(),
      toDate: currentTimeInTimezone.endOf('day').toDate(),
    };
  };

  const computeXLastWeeks = (weeks: number) => {
    const fromDate = currentTimeInTimezone
      .subtract(weeks, 'weeks')
      .startOf('week')
      .toDate();
    const toDate = currentTimeInTimezone.subtract(1, 'week').endOf('week').toDate();

    return {
      fromDate,
      toDate,
    };
  };

  const computeXLastMonths = (months: number) => {
    const fromDate = currentTimeInTimezone
      .subtract(months, 'months')
      .startOf('month')
      .toDate();
    const toDate = currentTimeInTimezone.subtract(1, 'month').endOf('month').toDate();

    return {
      fromDate,
      toDate,
    };
  };

  const computeToday = () => {
    return {
      fromDate: currentTimeInTimezone.startOf('day').toDate(),
      toDate: currentTimeInTimezone.endOf('day').toDate(),
    };
  };

  const computeYesterday = () => {
    const yesterdayDateObject = currentTimeInTimezone.subtract(1, 'day');

    return {
      fromDate: yesterdayDateObject.startOf('day').toDate(),
      toDate: yesterdayDateObject.endOf('day').toDate(),
    };
  };

  const computeThisMonth = () => {
    return {
      fromDate: currentTimeInTimezone.startOf('month').toDate(),
      toDate: currentTimeInTimezone.endOf('day').toDate(),
    };
  };

  const computeThisQuarter = () => {
    return {
      fromDate: currentTimeInTimezone.startOf('quarter').toDate(),
      toDate: currentTimeInTimezone.endOf('day').toDate(),
    };
  };

  const computeThisYear = () => {
    return {
      fromDate: currentTimeInTimezone.startOf('year').toDate(),
      toDate: currentTimeInTimezone.endOf('day').toDate(),
    };
  };

  const computeBetween = (between: BaseAdvancedDateIsBetweenWhereInput) => {
    const { from, to } = between;

    return {
      fromDate: dayjs(from).tz(timezone).startOf('day').toDate(),
      toDate: dayjs(to).tz(timezone).endOf('day').toDate(),
    };
  };

  const getQueryFilterDate = (date: Date): Date => dayjs(date).tz(timezone).toDate();

  return {
    timezone,
    timezoneCurrentDateObject: currentTimeInTimezone,
    computeXLastDays,
    computeXLastWeeks,
    computeXLastMonths,
    computeToday,
    computeYesterday,
    computeThisMonth,
    computeThisQuarter,
    computeThisYear,
    computeBetween,
    getQueryFilterDate,
  };
};

export const getPeriodDateRange = (input: {
  timezone: string;
  period: BaseMetricPeriodWhereInput;
}) => {
  const { period, timezone } = input;

  const {
    computeXLastDays,
    computeXLastWeeks,
    computeXLastMonths,
    computeToday,
    computeYesterday,
    computeThisMonth,
    computeThisQuarter,
    computeThisYear,
    computeBetween,
  } = createDateWhereInputHelper({
    timezone,
  });

  if (period._isLastXDays !== undefined && period._isLastXDays !== null) {
    return computeXLastDays(period._isLastXDays);
  }

  if (period._isLastXWeeks !== undefined && period._isLastXWeeks !== null) {
    return computeXLastWeeks(period._isLastXWeeks);
  }

  if (period._isLastXMonths !== undefined && period._isLastXMonths !== null) {
    return computeXLastMonths(period._isLastXMonths);
  }

  if (period._isToday) {
    return computeToday();
  }

  if (period._isYesterday) {
    return computeYesterday();
  }

  if (period._isThisMonth) {
    return computeThisMonth();
  }

  if (period._isThisQuarter) {
    return computeThisQuarter();
  }

  if (period._isThisYear) {
    return computeThisYear();
  }

  if (period._isBetween) {
    return computeBetween(period._isBetween);
  }
};

const isFirstDayOfMonth = (date: dayjs.Dayjs): boolean => {
  const d = dayjs(date);
  return d.isSame(d.startOf('month'), 'day');
};

const isLastDayOfMonth = (date: dayjs.Dayjs): boolean => {
  const d = dayjs(date);
  return d.isSame(d.endOf('month'), 'day');
};

const isFirstDayOfQuarter = (date: dayjs.Dayjs): boolean => {
  const d = dayjs(date);
  return d.isSame(d.startOf('quarter'), 'day');
};

const isLastDayOfQuarter = (date: dayjs.Dayjs): boolean => {
  const d = dayjs(date);
  return d.isSame(d.endOf('quarter'), 'day');
};

export type AvailableComparePeriodsForPeriod = {
  previousPeriod: {
    fromDate: Dayjs;
    toDate: Dayjs;
  };
  previousMonth?: {
    fromDate: Dayjs;
    toDate: Dayjs;
  };
  previousQuarter?: {
    fromDate: Dayjs;
    toDate: Dayjs;
  };
  previousYear?: {
    fromDate: Dayjs;
    toDate: Dayjs;
  };
};

// Spec document:
// https://leadgogo.atlassian.net/wiki/spaces/LGG/pages/635830273/Dashboard+compare+period+options+rules
export const getAvailableComparePeriodsForPeriod = (input: {
  timezone: string;
  fromDate: string;
  toDate: string;
}): AvailableComparePeriodsForPeriod => {
  const { timezone } = input;
  const fromDate = dayjs(input.fromDate).tz(timezone);
  const toDate = dayjs(input.toDate).tz(timezone);
  const diffInSeconds = Math.round(toDate.diff(fromDate, 'seconds', true));

  const compareDateRanges: {
    previousPeriod: { fromDate: Dayjs; toDate: Dayjs };
    previousMonth?: { fromDate: Dayjs; toDate: Dayjs };
    previousQuarter?: { fromDate: Dayjs; toDate: Dayjs };
    previousYear?: { fromDate: Dayjs; toDate: Dayjs };
  } = {
    previousPeriod: {
      fromDate: fromDate.subtract(diffInSeconds, 'seconds'),
      toDate: toDate.subtract(diffInSeconds, 'seconds'),
    },
  };

  const areDatesInSameYear = fromDate.year() === toDate.year();

  const areDatesInSameMonth = fromDate.month() === toDate.month() && areDatesInSameYear;

  if (areDatesInSameMonth) {
    const previousMonthFromDate = fromDate.subtract(1, 'month');
    const previousMonthToDate = toDate.subtract(1, 'month');

    if (isFirstDayOfMonth(fromDate) && isLastDayOfMonth(toDate)) {
      compareDateRanges.previousMonth = {
        fromDate: previousMonthFromDate.startOf('month'),
        toDate: previousMonthToDate.set('date', previousMonthToDate.daysInMonth()),
      };
    } else {
      compareDateRanges.previousMonth = {
        fromDate: previousMonthFromDate,
        toDate: previousMonthToDate,
      };
    }
  }

  const areDatesInSameQuarter =
    fromDate.quarter() === toDate.quarter() && areDatesInSameYear;

  if (areDatesInSameQuarter) {
    const previousQuarterFromDate = fromDate.subtract(1, 'quarter');
    const previousQuarterToDate = toDate.subtract(1, 'quarter');

    if (isFirstDayOfQuarter(fromDate) && isLastDayOfQuarter(toDate)) {
      compareDateRanges.previousQuarter = {
        fromDate: previousQuarterFromDate.startOf('quarter'),
        toDate: previousQuarterToDate.endOf('quarter'),
      };
    } else if (
      areDatesInSameMonth &&
      isFirstDayOfMonth(fromDate) &&
      isLastDayOfMonth(toDate)
    ) {
      compareDateRanges.previousQuarter = {
        fromDate: previousQuarterFromDate.startOf('month'),
        toDate: previousQuarterToDate.set('date', previousQuarterToDate.daysInMonth()),
      };
    } else {
      const quarterDiffInSeconds = Math.round(
        toDate.diff(previousQuarterToDate, 'seconds', true),
      );

      compareDateRanges.previousQuarter = {
        fromDate: fromDate.subtract(quarterDiffInSeconds, 'seconds'),
        toDate: previousQuarterToDate,
      };
    }
  }

  if (areDatesInSameYear) {
    const previousYearFromDate = fromDate.subtract(1, 'year');
    const previousYearToDate = toDate.subtract(1, 'year');

    if (areDatesInSameMonth && isFirstDayOfMonth(fromDate) && isLastDayOfMonth(toDate)) {
      compareDateRanges.previousYear = {
        fromDate: previousYearFromDate.startOf('month'),
        toDate: previousYearToDate.set('date', previousYearToDate.daysInMonth()),
      };
    } else {
      compareDateRanges.previousYear = {
        fromDate: previousYearFromDate,
        toDate: previousYearToDate,
      };
    }
  }

  return compareDateRanges;
};
